/**
 * WAS DER TIEFBAU DRAUSSEN VORGEFUNDEN HAT - AN EINER STELLE.
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (18.09.2026)
 *   „bei dem abmelden der beim tiefbau fehlen die anderen checkboxen, ich
 *    sagte doch, das er die luftprüfung durchführen muss, pipe im haus, kabel
 *    auch im haus, da ist nur die angabe zur pipe drin."
 *
 * ER HAT RECHT, UND DER FEHLER WAR EINE FRAGE DES ORTES. Die 3 Angaben gibt
 * es seit der .166 - sie standen aber NUR im Fenster „Fertig melden". Der
 * Tiefbau, der von der Baustelle zurückmeldet, öffnet „Zurückmelden", und
 * dort standen nur die 4 Pipe-Angaben aus der Planung. **Wer sich abmeldet,
 * beantwortet dieselben Fragen, egal durch welche Tür er geht.**
 *
 * DESHALB STEHEN SIE HIER UND NICHT ZWEIMAL - dieselbe Lehre wie bei
 * `TechnikFelder` in `teile/Rueckmeldung.tsx`: zwei Abschriften laufen beim
 * ersten Nachtrag auseinander, und dann fragt dasselbe Formular je nach Weg
 * etwas anderes ab. Gebucht werden sie serverseitig von genau einer Regel,
 * `api/src/regeln/vorgefunden.ts`.
 *
 * UND DAS KABEL FRAGT JETZT IMMER.
 * ----------------------------------------------------------------------------
 * Hier stand die Bedingung „nur, wenn es den Ordner Montage/01
 * Einblasprotokoll überhaupt gibt". Der Gedanke war richtig (ein Haken ohne
 * Wirkung ist ein Versprechen, das nicht gilt) und der Ort war falsch: an
 * einem Auftrag, an dem der Tiefbau steht, ist der Montageordner oft noch
 * nicht angelegt - und dann fehlte genau die Frage, die Marvin gestellt
 * haben wollte. **Ob das Kabel im Haus lag, weiss der, der davorsteht, und
 * nicht der, der später den Ordner aufmacht.** Der Haken steht jetzt da,
 * solange die Angabe nicht schon in der Akte steht; was er freistellt,
 * entscheidet weiterhin `regeln/ordner.ts`.
 */

import { Haken } from './Formular.tsx';

/** Die 3 Angaben, wie sie zum Server gehen. */
export interface VorgefundenEingabe {
  kabel: boolean;
  keinTiefbau: boolean;
  /** `null` heisst „nicht geprüft" - und das ist auch eine Antwort. */
  druckluft: null | 'dicht' | 'zu';
}

export const VORGEFUNDEN_LEER: VorgefundenEingabe = {
  kabel: false, keinTiefbau: false, druckluft: null,
};

/** Was davon schon in der Akte steht. Beide Fenster liefern dieselbe Form. */
export interface VorgefundenStand {
  freistellung?: { kabelImHaus?: boolean; keinTiefbau?: boolean } | null;
  druckluft?: { geprueft?: boolean; dicht?: boolean | null;
                name?: string | null } | null;
}

/** Der Rumpf für den Server. Leere Angaben gehen NICHT mit. */
export function vorgefundenRumpf(w: VorgefundenEingabe) {
  return {
    ...(w.kabel ? { kabel_im_haus: true } : {}),
    ...(w.keinTiefbau ? { kein_tiefbau: true } : {}),
    ...(w.druckluft ? { druckluft_geprueft: true,
                        druckluft_dicht: w.druckluft === 'dicht' } : {}),
  };
}

export function Vorgefunden({ stand, wert, setze }: {
  stand: VorgefundenStand | null;
  wert: VorgefundenEingabe;
  setze: (w: VorgefundenEingabe) => void;
}) {
  const schonKabel = !!stand?.freistellung?.kabelImHaus;
  const schonTiefbau = !!stand?.freistellung?.keinTiefbau;
  const schonLuft = !!stand?.druckluft?.geprueft;

  return (
    <>
      {/* 1. DAS KABEL LAG SCHON IM HAUS.
          ORIGINALTEXT MARVIN (10.09.2026)
            „bei den monteuren fehlt beim fertigmelden der punkt «Kabel schon
             bereits vorhanden» damit kein einblasprotokoll hochgeladen werden
             muss."
          Die Regel gab es (`regeln/ordner.ts`); was gefehlt hat, ist der Weg,
          es zu SAGEN - er lag im Block „Technik" der Auftragsakte, also im
          Innendienst, an einer Stelle, an der der Monteur nicht vorbeikommt.
          **Ein Weg, den man nicht findet, ist so gut wie keiner.** */}
      {!schonKabel && (
        <div style={{ marginTop: 12 }}>
          <Haken name="kabel_im_haus"
                 titel="Das Kabel lag schon im Haus"
                 wert={wert.kabel}
                 setze={(v) => setze({ ...wert, kabel: v })}
                 hinweis={'Dann wurde nichts eingeblasen, und der Ordner '
                          + '„Montage/01 Einblasprotokoll" ist kein '
                          + 'Pflichtordner mehr. Die Angabe steht danach in '
                          + 'der Akte und im Verlauf; zurücknehmen kann sie '
                          + 'der Innendienst unter Bearbeiten, Reiter '
                          + 'Technik.'} />
        </div>
      )}
      {/* STAND ES SCHON IN DER AKTE, steht hier keine Frage mehr, sondern die
          Antwort: ein Haken, der nichts mehr bewirkt, sieht aus wie ein
          Haken, den man vergessen hat. */}
      {schonKabel && (
        <div className="hinweis" style={{ marginTop: 12 }}>
          Das Kabel lag schon im Haus. Ein Einblasprotokoll wird deshalb nicht
          verlangt.
        </div>
      )}

      {/* 2. KEIN TIEFBAU NÖTIG - DIE PIPE LAG SCHON DA.
          ORIGINALTEXT MARVIN (18.09.2026)
            „wenn sie kein Tiefbau gemacht haben, weil die Pipe bereits im
             haus  war, auch als auswahl dazu"
          ER SETZT ZWEI ANGABEN AUF EINMAL, und das ist Absicht: es ist eine
          Feststellung, kein Schalter. „Kein Tiefbau" allein hiesse „aus
          irgendeinem Grund"; hier ist der Grund die vorgefundene Pipe, und
          genau den braucht die Montage. */}
      {!schonTiefbau && (
        <div style={{ marginTop: 12 }}>
          <Haken name="kein_tiefbau"
                 titel="Kein Tiefbau nötig, die Pipe lag schon da"
                 wert={wert.keinTiefbau}
                 setze={(v) => setze({ ...wert, keinTiefbau: v })}
                 hinweis={'Dann fallen alle Pflichtordner des Bereichs '
                          + 'Tiefbau weg. Tragen Sie ein, was Sie '
                          + 'vorgefunden haben: ohne Länge, Stärke, Verband '
                          + 'und Röhrchen weiß die Montage nicht, womit sie '
                          + 'es zu tun hat. Die Angabe steht danach in der '
                          + 'Akte; zurücknehmen kann sie der Innendienst '
                          + 'unter Bearbeiten.'} />
        </div>
      )}
      {schonTiefbau && (
        <div className="hinweis" style={{ marginTop: 12 }}>
          Es ist eingetragen, dass kein Tiefbau nötig war. Die Pflichtordner
          des Bereichs Tiefbau entfallen deshalb.
        </div>
      )}

      {/* 3. DIE DRUCKLUFTPRÜFUNG.
          ORIGINALTEXT MARVIN (18.09.2026)
            „und dass sie die druckluftprüfung gemacht haben ... denn wenn der
             tiefbau schon vor ort ist, dann muss er das auf jeden fall
             prüfen, sonst fährt die montage umsonst hin"

          **SIE STELLT KEINEN ORDNER FREI.** Sie ist eine Auskunft an die
          Montage und steht danach im Block „Tiefbau" der Akte - genau dort,
          wo jemand nachsieht, bevor er losfährt.

          ZWEI KNÖPFE UND KEIN HAKEN. Ein Haken „geprüft" ohne Ergebnis wäre
          die halbe Auskunft, und zwar die Hälfte, wegen der die Montage
          trotzdem umsonst hinfährt. Eine Prüfung hat ein Ergebnis; wer keins
          nennt, hat nicht geprüft. */}
      {!schonLuft && (
        <div style={{ marginTop: 12 }}>
          <div className="klein"><b>Druckluftprüfung der Pipe</b></div>
          <div className="reihe" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button type="button"
                    className={'knopf' + (wert.druckluft === 'dicht' ? ' haupt' : '')}
                    onClick={() => setze({ ...wert,
                      druckluft: wert.druckluft === 'dicht' ? null : 'dicht' })}>
              Gemacht, Pipe ist durchgängig
            </button>
            <button type="button"
                    className={'knopf' + (wert.druckluft === 'zu' ? ' warnung' : '')}
                    onClick={() => setze({ ...wert,
                      druckluft: wert.druckluft === 'zu' ? null : 'zu' })}>
              Gemacht, Pipe ist NICHT durchgängig
            </button>
          </div>
          <p className="klein leise" style={{ marginTop: 4 }}>
            Wer ohnehin davorsteht, prüft sie. Eine vorgefundene Pipe sieht
            von aussen aus wie eine gute; ob sie durchgängig ist, weiss nur,
            wer Druckluft daraufgibt. Ohne diese Angabe fährt die Montage
            unter Umständen umsonst hin, und der Tiefbau muss ein zweites Mal
            kommen. Nichts zu wählen ist auch eine Antwort: dann steht dort
            „nicht geprüft".
          </p>
        </div>
      )}
      {schonLuft && (
        <div className={'hinweis' + (stand?.druckluft?.dicht ? '' : ' warnung')}
             style={{ marginTop: 12 }}>
          Druckluftprüfung: die Pipe ist
          {stand?.druckluft?.dicht ? ' durchgängig' : ' NICHT durchgängig'}
          {stand?.druckluft?.name ? ` (gemeldet von ${stand.druckluft.name})` : ''}.
        </div>
      )}
    </>
  );
}
